import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  AudioLines,
  BarChart3,
  Building2,
  ChevronDown,
  ChevronRight,
  Database,
  FolderKanban,
  Grid2X2,
  LayoutDashboard,
  LibraryBig,
  LogOut,
  Menu,
  ScrollText,
  Settings,
  Sparkles,
  UserPlus,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { useLocale } from '../hooks/useLocale';
import { allowed, type PermReq } from '../lib/perms';

interface LayoutProps {
  children: React.ReactNode;
  user?: { email: string; name?: string | null; permissions?: string[] };
  onLogout?: () => void;
}

export default function Layout({ children, user, onLogout }: LayoutProps) {
  const location = useLocation();
  const { locale, isArabic, toggleLocale } = useLocale();
  const perms = user?.permissions ?? [];
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // The sidebar is grouped into collapsible, labelled sections. Each item carries
  // the permission needed to see it; sections with no visible items are hidden.
  type NavItem = { path: string; label: string; icon: typeof Grid2X2; exact?: boolean; perm: PermReq };
  type NavSection = { key: string; title: string; items: NavItem[] };
  const allSections: NavSection[] = [
    {
      key: 'overview', title: isArabic ? 'نظرة عامة' : 'Overview', items: [
        { path: '/', label: isArabic ? 'لوحة التحكم' : 'Dashboard', icon: Grid2X2, exact: true, perm: null },
        { path: '/analytics', label: isArabic ? 'التحليلات' : 'Analytics', icon: BarChart3, perm: 'anyWorkflow' },
      ],
    },
    {
      key: 'production', title: isArabic ? 'الإنتاج' : 'Production', items: [
        { path: '/new-batch', label: isArabic ? 'بدء دفعة' : 'New Batch', icon: Sparkles, perm: 'intake' },
        { path: '/intake', label: isArabic ? 'الاستقبال' : 'Intake', icon: FolderKanban, perm: 'intake' },
        { path: '/metadata', label: isArabic ? 'البيانات' : 'Metadata', icon: Database, perm: 'metadata' },
        { path: '/matching', label: isArabic ? 'المطابقة' : 'Matching', icon: LibraryBig, perm: 'matching' },
        { path: '/processing', label: isArabic ? 'المعالجة' : 'Processing', icon: Activity, perm: 'processing' },
      ],
    },
    {
      key: 'catalog', title: isArabic ? 'الفهرس' : 'Catalog', items: [
        { path: '/books', label: isArabic ? 'العناوين' : 'Books', icon: AudioLines, perm: 'anyWorkflow' },
        { path: '/artifacts', label: isArabic ? 'التخزين' : 'Storage', icon: FolderKanban, perm: 'anyWorkflow' },
        { path: '/legacy-import', label: isArabic ? 'استيراد كتب قديمة' : 'Legacy Books', icon: Upload, exact: true, perm: 'users' },
      ],
    },
    {
      key: 'studios', title: isArabic ? 'الاستوديوهات' : 'Studios', items: [
        { path: '/studios/dashboard', label: isArabic ? 'لوحة الاستوديوهات' : 'Dashboard', icon: LayoutDashboard, exact: true, perm: 'studios' },
        { path: '/studios', label: isArabic ? 'الاستوديوهات' : 'Studios', icon: Building2, perm: 'studios' },
        { path: '/studios/acquisition', label: isArabic ? 'الاقتناء' : 'Acquisition', icon: UserPlus, exact: true, perm: 'studios' },
        { path: '/legacy-studios-import', label: isArabic ? 'استيراد استوديوهات' : 'Legacy Studios', icon: Upload, exact: true, perm: 'users' },
      ],
    },
    {
      key: 'admin', title: isArabic ? 'الإدارة' : 'Administration', items: [
        { path: '/users', label: isArabic ? 'المستخدمون' : 'Users', icon: Users, perm: 'users' },
        { path: '/audit', label: isArabic ? 'سجل الإجراءات' : 'Audit Log', icon: ScrollText, perm: 'users' },
        { path: '/settings', label: isArabic ? 'الإعدادات' : 'Settings', icon: Settings, perm: 'users' },
      ],
    },
  ];
  // Keep only items the user may see, then drop any now-empty section.
  const sections = allSections
    .map((s) => ({ ...s, items: s.items.filter((it) => allowed(perms, it.perm)) }))
    .filter((s) => s.items.length > 0);

  // Sections are collapsed ("wrapped") by default; the group holding the current
  // route auto-expands so the active page stays visible.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(allSections.map((s) => s.key)));
  const toggleSection = (key: string) => setCollapsed((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // /studios owns /studios/:id (management); its sibling sub-routes are matched by
  // their own exact entries, so exclude them here to avoid a double highlight.
  const itemActive = (item: NavItem) => {
    if (item.exact) return location.pathname === item.path;
    if (item.path === '/studios') {
      return location.pathname === '/studios' || (location.pathname.startsWith('/studios/') && !['/studios/dashboard', '/studios/acquisition'].includes(location.pathname));
    }
    return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
  };

  // Auto-expand the section that contains the active route (on navigation).
  const activeSectionKey = sections.find((s) => s.items.some(itemActive))?.key;
  useEffect(() => {
    if (!activeSectionKey) return;
    setCollapsed((prev) => {
      if (!prev.has(activeSectionKey)) return prev;
      const next = new Set(prev);
      next.delete(activeSectionKey);
      return next;
    });
  }, [activeSectionKey]);

  return (
    <div className={`shell flex min-h-screen ${navOpen ? 'nav-open' : ''}`}>
      {/* Mobile backdrop */}
      {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} aria-hidden />}

      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="flex items-start justify-between gap-2">
            <img src="/samawy/assets/logo-primary.png" alt="Samawy" className="h-10 w-auto" />
            <button
              type="button"
              onClick={() => setNavOpen(false)}
              className="mobile-bar-icon-btn lg:hidden"
              aria-label={isArabic ? 'إغلاق القائمة' : 'Close menu'}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div>
            <p className="sidebar-brand">SAMAWY AUDIOBOOKS OPS</p>
            <p className="sidebar-subtitle">
              {isArabic ? 'منصة إدارة إنتاج الكتب الصوتية' : 'Audiobook Operations Platform'}
            </p>
          </div>
        </div>

        <nav className="sidebar-nav">
          {sections.map((section) => {
            const open = !collapsed.has(section.key);
            return (
              <div key={section.key} className="mb-1">
                <button
                  type="button"
                  onClick={() => toggleSection(section.key)}
                  className="flex items-center gap-1.5 w-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[color:var(--fg-2)] hover:text-[color:var(--samawy-ink)] transition-colors"
                  aria-expanded={open}
                >
                  {open ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                  <span className="flex-1 text-start">{section.title}</span>
                </button>
                {open && (
                  <div className="space-y-0.5">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const active = itemActive(item);
                      return (
                        <Link key={item.path} to={item.path} className={`sidebar-link ${active ? 'sidebar-link-active' : ''}`}>
                          <Icon className="sidebar-icon" />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer space-y-2">
          {user && (
            <div className="rounded-[14px] border border-slate-100 px-3 py-2.5 flex items-center justify-between gap-2">
              <div className="min-w-0">
                {user.name && <p className="text-xs font-semibold text-[color:var(--samawy-ink)] truncate">{user.name}</p>}
                <p className="text-xs text-[color:var(--fg-2)] truncate">{user.email}</p>
              </div>
              {onLogout && (
                <button type="button" onClick={onLogout} title={isArabic ? 'تسجيل الخروج' : 'Sign out'} className="shrink-0 text-[color:var(--fg-2)] hover:text-red-600 transition-colors">
                  <LogOut className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <button type="button" className="btn-secondary w-full justify-center" onClick={toggleLocale}>
            {locale === 'ar' ? 'EN' : 'AR'}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile bar */}
        <div className="mobile-bar">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="mobile-bar-icon-btn"
            aria-label={isArabic ? 'فتح القائمة' : 'Open menu'}
          >
            <Menu className="h-5 w-5" />
          </button>
          <img src="/samawy/assets/logo-primary.png" alt="Samawy" className="h-7 w-auto" />
          <button type="button" className="mobile-bar-icon-btn text-xs font-semibold px-2.5" onClick={toggleLocale}>
            {locale === 'ar' ? 'EN' : 'AR'}
          </button>
        </div>

        <header className="topbar">
          <div className="rounded-full bg-[rgba(11,128,255,0.08)] px-4 py-2 text-sm font-medium text-sky-700">
            {isArabic ? 'النظام المرجعي لعناوين سماوي ودفاتر التسليم' : 'Source of truth for Samawy audiobooks and delivery dossiers'}
          </div>
        </header>
        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
